import { ContentSchedule } from '_api/types';
import { DayOfWeekIndex } from '_constants';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

type Schedule = {
  start: Dayjs;
  end: Dayjs;
  daysOfTheWeek: string[];
  perDay: number;
};

const toParsedSchedule = (contentSchedule: ContentSchedule): Schedule => ({
  start: dayjs(contentSchedule.startDate),
  end: dayjs(contentSchedule.endDate),
  daysOfTheWeek: contentSchedule.schedule,
  perDay: contentSchedule.perDay,
});

export const generateLabels = (startDate: string, endDate: string): string[] => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const diffInDays = end.diff(start, 'day');
  const diffInMonths = end.diff(start, 'month');

  const labels: string[] = [];
  if (diffInDays <= 31) {
    for (
      let date = start;
      date.isBefore(end) || date.isSame(end);
      date = date.add(1, 'day')
    ) {
      labels.push(date.format('YYYY-MM-DD'));
    }
  } else if (diffInMonths <= 12) {
    for (
      let date = start.startOf('month');
      date.isBefore(end) || date.isSame(end);
      date = date.add(1, 'month')
    ) {
      labels.push(date.format('YYYY-MM'));
    }
  } else {
    for (
      let date = start.startOf('year');
      date.isBefore(end) || date.isSame(end);
      date = date.add(1, 'year')
    ) {
      labels.push(date.format('YYYY'));
    }
  }
  return labels;
};

export const parseAssignedData = (
  schedules: ContentSchedule[],
  startDate: string,
  endDate: string,
  stopDate: string,
  labels: string[],
): number[] => {
  const graphStart = dayjs(startDate);
  const graphEnd = dayjs(endDate);
  const isYear = graphEnd.diff(graphStart, 'month') === 11;
  const isWeekly = graphEnd.diff(graphStart, 'day') <= 7;

  const scheduled: number[] = isWeekly
    ? new Array(7).fill(0)
    : new Array(labels.length).fill(0);

  for (const sched of schedules.map(toParsedSchedule)) {
    const assignedData = sched.daysOfTheWeek;
    const maxValue = sched.perDay ? sched.perDay : 1;
    const schedStart = sched.start;
    const schedEnd = sched.end.isValid() ? sched.end : schedStart.add(2, 'year');

    if (isWeekly && assignedData) {
      const assignedDays = assignedData.map((day) => DayOfWeekIndex[day]);
      for (
        let currentDate = graphStart;
        currentDate.isSameOrBefore(stopDate);
        currentDate = currentDate.add(1, 'day')
      ) {
        const isInSchedule =
          currentDate.isSameOrAfter(schedStart) && currentDate.isBefore(schedEnd);
        if (isInSchedule && assignedDays.includes(currentDate.day())) {
          scheduled[currentDate.day()] = maxValue;
        }
      }
    } else if (!isYear && assignedData) {
      const assignedDays = assignedData.map((day) => DayOfWeekIndex[day]);
      let i = 0;
      for (
        let date = graphStart;
        date.isSameOrBefore(stopDate);
        date = date.add(1, 'day')
      ) {
        const isInSchedule = date.isSameOrAfter(schedStart) && date.isBefore(schedEnd);
        if (isInSchedule && assignedDays.includes(date.day())) {
          scheduled[i] = maxValue;
        }
        i++;
      }
    }
  }
  return scheduled;
};
